import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getGlobalConfig, setGlobalConfig } from "../../../datahandling/redux/globalConfig";
import { fetchConfig } from "../../../datahandling/redux/slice/config/helpers";
import type { Config } from "../../../datahandling/redux/slice/config/types";
import { registerAgGridLicense, registerAgChartsLicense } from "src/utility";
import { setConfigs } from "../../../datahandling/redux/slice/config";
import { selectConfigLoaded } from "../../../datahandling/redux/slice/config/selectors";
import type { RootState } from "../../../datahandling/redux/store";

/**
 * Hook that fetches and returns the global configuration.
 * Returns undefined while loading, or the Config object once fetched.
 * Also syncs with Redux store and registers AG Grid/Charts licenses.
 *
 * @returns Config | undefined
 */
export function useDomConfig(): Config | undefined {
	const dispatch = useDispatch();
	const configLoaded = useSelector((state: RootState) => selectConfigLoaded(state));
	const [config, setConfig] = useState<Config | undefined>(() => getGlobalConfig());

	useEffect(() => {
		if (config !== undefined) return;

		let cancelled = false;

		const loadConfig = async () => {
			try {
				const fetchedConfig = await fetchConfig();

				if (!cancelled && fetchedConfig) {
					setGlobalConfig(fetchedConfig);
					setConfig(fetchedConfig);
					dispatch(setConfigs(fetchedConfig));

					registerAgGridLicense();
					registerAgChartsLicense();
				}
			} catch (error) {
				console.error("Failed to load global config:", error);
				// Config remains undefined on error
			}
		};

		loadConfig();

		return () => {
			cancelled = true;
		};
	}, [config, dispatch]);

	// Sync with Redux if config exists in global but not yet loaded in Redux
	useEffect(() => {
		if (config && !configLoaded) {
			dispatch(setConfigs(config));
		}
	}, [config, configLoaded, dispatch]);

	return config;
}
